/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: default report model (the embedded `data` template).
// Source: cardiac_report_v0_5_6_dev_2.html lines 1992-2293.
// The import flow REQUIRES this template: applyPcgFeature merges evidence into
// existing features; features with no evidence keep these demo values.
// HAZARD (flagged in MODULE-MAP): the prototype's template ships demo observed
// values (67 bpm, "1 : 1.7", demo questionnaire/recordings). This factory strips
// the demo recordings/questionnaire by default and blanks observed values unless
// `includeDemoValues` is set — a deliberate deviation for production safety.

const FEATURE_TEMPLATE = [
  {
    name: "Signal Quality",
    normalRange: "",
    observed: "Fair",
    status: "amber",
    scaleType: "unidirectional",
    type: "signal",
    interpretation: "Recording adequacy and guide availability",
    isHidden: false,
    showInBasic: false,
    showInPremium: false,
    showNumericInPatient: false,
    aiStatus: "borderline",
    dataSource: "viewer",
    referenceSource: "PCG Viewer signal reliability",
    patientNormal: "The recording was clear enough for timing and structural-sound screening.",
    patientBorderline:
      "The recording was clear enough for some screening guidance, but one or more guide layers were limited.",
    patientAbnormal:
      "The recording was not clear enough for screening guidance. Repeat the recording or ask for clinician review.",
    providerNormal: "Good.",
    providerBorderline: "Fair.",
    providerAbnormal: "Poor signal quality; repeat recording required before report submission.",
  },
  {
    name: "Heart Rate",
    normalRange: "60 – 100 bpm",
    observed: "67 bpm",
    status: "green",
    scaleType: "bidirectional",
    type: "bpm",
    interpretation: "Average heart rate across accepted recordings",
    isHidden: false,
    showInBasic: true,
    showInPremium: true,
    showNumericInPatient: true,
    aiStatus: "normal",
    dataSource: "viewer",
    referenceSource: "PCG Viewer timing guide",
    patientNormal: "Your average heart rate during the test was 67 bpm.",
    patientBorderline:
      "Your result appeared in the borderline range. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
    patientAbnormal:
      "Your average heart rate was outside the typical adult resting range. Please discuss this report with your healthcare provider.",
    providerNormal:
      "Average PCG-guided rate is within typical adult resting range. This is not an ECG rhythm diagnosis.",
    providerBorderline:
      "Average PCG-guided rate is near or slightly outside typical adult resting range; correlate clinically.",
    providerAbnormal:
      "Average PCG-guided rate is outside typical adult resting range; correlate clinically and consider ECG-based evaluation if indicated.",
  },
  {
    name: "Timing Summary",
    normalRange: "",
    observed: "Regular",
    status: "green",
    scaleType: "unidirectional",
    type: "timing",
    interpretation: "Heart rate, cardiac cycle timing, and rhythm regularity summary",
    isHidden: false,
    showInBasic: true,
    showInPremium: true,
    showToPatient: false,
    showNumericInPatient: false,
    aiStatus: "normal",
    dataSource: "viewer",
    referenceSource: "PCG Viewer cardiac timing guide layer",
    patientNormal: "The timing pattern was regular during the test.",
    patientBorderline:
      "The timing pattern showed some variation. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
    patientAbnormal: "The timing pattern was irregular. Please discuss this report with your healthcare provider.",
    providerNormal: "PCG timing pattern regular. No ECG rhythm diagnosis implied.",
    providerBorderline: "PCG timing pattern variable. Correlate clinically. No ECG rhythm diagnosis implied.",
    providerAbnormal: "PCG timing pattern irregular. Consider ECG-based rhythm assessment if clinically indicated.",
  },
  {
    name: "SYS:DIA Ratio",
    normalRange: "0.40 – 0.67",
    displayReferenceRange: "Expected resting range: 1 : 1.5 to 1 : 2.5",
    observed: "1 : 1.7",
    status: "green",
    scaleType: "bidirectional",
    type: "ratio",
    interpretation: "Systolic duration divided by diastolic duration across accepted recordings",
    isHidden: false,
    showInBasic: false,
    showInPremium: true,
    showNumericInPatient: true,
    aiStatus: "normal",
    dataSource: "viewer",
    referenceSource: "PCG Viewer cardiac cycle timing guide",
    patientNormal: "Your cardiac cycle timing relationship appeared normal for a resting recording.",
    patientBorderline:
      "Your cardiac cycle timing relationship was in the borderline range for a resting recording. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
    patientAbnormal:
      "Your cardiac cycle timing relationship was outside the expected resting range. Please discuss this report with your healthcare provider.",
    providerNormal: "SYS:DIA timing ratio within expected resting screening range.",
    providerBorderline:
      "SYS:DIA timing ratio near expected resting screening range. Correlate with heart rate and cycle regularity.",
    providerAbnormal:
      "SYS:DIA timing ratio outside expected resting screening range. Correlate with heart rate, cycle regularity, and clinical context.",
  },
  {
    name: "SYS:DIA Duration",
    normalRange: "500 – 700 ms",
    displayReferenceRange: "Expected systolic and diastolic durations vary with heart rate",
    observed: "SYS 320 ms · DIA 580 ms",
    status: "green",
    scaleType: "bidirectional",
    type: "duration",
    interpretation: "Systolic and diastolic phase durations across accepted recordings",
    isHidden: false,
    showInBasic: false,
    showInPremium: true,
    showNumericInPatient: false,
    aiStatus: "normal",
    dataSource: "viewer",
    referenceSource: "PCG Viewer cardiac cycle timing guide",
    patientNormal: "The squeezing and refilling timing pattern appeared normal for the observed heart rate.",
    patientBorderline:
      "The squeezing and refilling timing pattern was in the borderline range for the observed heart rate. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
    patientAbnormal:
      "The refilling window appeared shortened or otherwise outside the expected range for the observed heart rate. Please discuss this report with your healthcare provider.",
    providerNormal: "Mean SYS and DIA durations are within expected screening range for the observed heart rate.",
    providerBorderline:
      "Mean SYS and DIA durations are near expected screening range for the observed heart rate. Correlate clinically.",
    providerAbnormal:
      "Mean SYS and DIA durations are outside expected screening range for the observed heart rate. Correlate clinically.",
  },
  {
    name: "Cycle Regularity",
    normalRange: ">= 85% regular",
    displayReferenceRange: "Experimental PCG timing variability bands: Normal · Moderate · High",
    observed: "92%",
    status: "green",
    scaleType: "regularity",
    type: "percentage",
    interpretation: "PCG-derived S1-to-S1 cycle timing proxy across accepted recordings",
    isHidden: false,
    showInBasic: false,
    showInPremium: true,
    showNumericInPatient: false,
    aiStatus: "normal",
    dataSource: "viewer",
    referenceSource: "PCG Viewer timing consistency guide",
    patientNormal:
      "The timing from one heartbeat to the next appeared mostly steady in the heart-sound recording. This is not an ECG rhythm test.",
    patientBorderline:
      "The heart-sound timing pattern showed some variation. Please discuss this report with your healthcare provider if you have symptoms or concerns.",
    patientAbnormal:
      "The heart-sound timing pattern appeared irregular. Please discuss this report with your healthcare provider.",
    providerNormal: "Normal PCG timing variability. PCG timing proxy; not ECG HRV or ECG rhythm diagnosis.",
    providerBorderline: "Moderate PCG timing variability. PCG timing proxy; not ECG HRV or ECG rhythm diagnosis.",
    providerAbnormal: "High PCG timing variability. PCG timing proxy; not ECG HRV or ECG rhythm diagnosis.",
  },
  {
    name: "S1/S2 Intensity",
    normalRange: "1.0 – 2.0",
    observed: "1.9",
    status: "green",
    scaleType: "bidirectional",
    type: "ratio",
    interpretation: "Relative S1 and S2 sound intensity",
    isHidden: false,
    showInBasic: false,
    showInPremium: true,
    showToPatient: false,
    showNumericInPatient: true,
    aiStatus: "normal",
    dataSource: "viewer",
    referenceSource: "PCG Viewer S1/S2 amplitude guide",
    patientNormal:
      "The relative intensity of the first and second main heart sounds was within the expected screening range.",
    patientBorderline:
      "The relative intensity of the first and second main heart sounds was somewhat outside the expected screening range.",
    patientAbnormal:
      "The relative intensity of the first and second main heart sounds was outside the expected screening range and should be reviewed.",
    providerNormal: "S1/S2 intensity ratio is within first-pass screening guardrails.",
    providerBorderline: "S1/S2 intensity ratio is borderline. Correlate clinically.",
    providerAbnormal:
      "S1/S2 intensity ratio is outside first-pass screening guardrails. This is a PCG screening cue for review.",
  },
  {
    name: "Structural Sound",
    normalRange: "",
    observed: "Possible repeating extra sound pattern",
    status: "amber",
    scaleType: "unidirectional",
    type: "structural-sound",
    interpretation: "Additional sound patterns between or around the usual heart sounds",
    isHidden: false,
    showInBasic: true,
    showInPremium: true,
    showNumericInPatient: false,
    aiStatus: "borderline",
    dataSource: "viewer",
    referenceSource: "ACT murmur detection plus PCG Viewer structural-sound guide layer",
    patientNormal:
      "No consistently recurring additional sound pattern was identified between or around the usual heart sounds.",
    patientBorderline:
      "An additional sound pattern was identified between or around the usual heart sounds across multiple heartbeats.",
    patientAbnormal:
      "A consistent additional sound pattern was identified between or around the usual heart sounds across most reviewed heartbeats.",
    providerNormal:
      "No prominent repeatable structural-sound pattern identified. This does not exclude structural pathology.",
    providerBorderline:
      "Borderline structural-sound repeatability identified by cycle repeatability threshold. Energy strength remains supporting evidence. Correlate with examination, history, and symptoms.",
    providerAbnormal:
      "Prominent repeatable abnormal acoustic pattern identified, sometimes associated with altered intracardiac blood flow. Correlate clinically.",
  },
]

export function createDefaultReport({ includeDemoValues = false } = {}) {
  const features = JSON.parse(JSON.stringify(FEATURE_TEMPLATE)).map((feature) => {
    feature.noteToPatient = ""
    feature.noteToDoctor = ""
    feature.sources = []
    if (!includeDemoValues) {
      // Deviation from the prototype: blank demo observed values so a feature
      // that receives no evidence renders as "not assessed", not as fake data.
      feature.observed = ""
      feature.status = "grey"
    }
    return feature
  })
  return {
    reportPackage: "basic",
    summary: { state: "", patientText: "", providerText: "" },
    followUpNote: { state: "", patientText: "", providerText: "" },
    features,
    patientNotes: "",
    providerNotes: "",
    recordings: [],
    recordingNotes: [],
    recordingNoteInstruction: "",
    questionnaire: null,
    longitudinalContext: {
      hasPrior: false,
      summary: "No prior Medaica exam available for comparison.",
      trendWindow: null,
      changes: [],
      priorExams: [],
      priorQuestionnaires: [],
    },
  }
}
